import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  MEMBER_QUERY_KEYS,
  MEMBERSHIP_QUERY_KEYS,
  MEMBER_PHOTO_QUERY_KEYS,
  CHECK_IN_QUERY_KEYS,
  CLASS_BOOKING_QUERY_KEYS,
  PT_SESSION_QUERY_KEYS,
  PT_SESSION_PACK_QUERY_KEYS,
  REPORT_QUERY_KEYS,
} from '@/constants';

/**
 * Returns an `invalidate` function that refreshes every per-member query
 * shown on the Member Profile screen. Call after any mutation performed
 * while viewing a member so that subsequently-mounted tabs/modals (e.g.
 * the Check-In modal opened right after adding a membership) read fresh
 * data from the cache rather than the stale snapshot from before.
 *
 * Prefix-only keys (e.g. `['memberships']`) take advantage of TanStack
 * Query's fuzzy matching to also invalidate id-scoped variants in one go.
 */
export function useInvalidateMemberProfile() {
  const queryClient = useQueryClient();

  return useCallback(
    (memberId: string) => {
      if (!memberId) return;

      const keys: (string | readonly unknown[])[] = [
        // Member record + member-scoped specialized queries
        MEMBER_QUERY_KEYS.BY_ID(memberId),
        MEMBER_QUERY_KEYS.PRIVACY_SETTINGS(memberId),
        MEMBER_QUERY_KEYS.DELETION_IMPACT(memberId),
        MEMBER_QUERY_KEYS.CHECK_INS(memberId),
        MEMBER_QUERY_KEYS.NOTES(memberId),
        MEMBER_QUERY_KEYS.ACTIVITY_LOGS(memberId),

        // Memberships for this member + cross-cutting membership lists
        MEMBERSHIP_QUERY_KEYS.BY_MEMBER(memberId),
        [MEMBERSHIP_QUERY_KEYS.ALL],
        ['bulk-member-memberships'],
        ['membership'],
        ['membership-receipts'],
        [MEMBERSHIP_QUERY_KEYS.FREEZES_PREFIX],
        [MEMBERSHIP_QUERY_KEYS.FREEZE_SUMMARY_PREFIX],
        [MEMBERSHIP_QUERY_KEYS.ACTIVITY_LOGS_PREFIX],
        [MEMBERSHIP_QUERY_KEYS.ACTIVE_MEMBER_GROWTH],
        [REPORT_QUERY_KEYS.MRR],

        // Photos
        MEMBER_PHOTO_QUERY_KEYS.WEBCAM_BY_MEMBER(memberId),
        MEMBER_PHOTO_QUERY_KEYS.ALL_WEBCAM_BY_MEMBER(memberId),
        ['bulk-member-webcam-photos'],
        ['member-uploaded-photos', memberId],
        MEMBER_PHOTO_QUERY_KEYS.ALL_PROFILE_BY_MEMBER(memberId),
        ['bulk-member-profile-photos'],

        // Check-ins (member-scoped + global counters that the profile shows)
        [CHECK_IN_QUERY_KEYS.ALL],
        [CHECK_IN_QUERY_KEYS.IN_THE_HOUSE],
        [CHECK_IN_QUERY_KEYS.LAST_30_DAYS],
        [CHECK_IN_QUERY_KEYS.TODAY],

        // Class bookings tab
        CLASS_BOOKING_QUERY_KEYS.BY_MEMBER(memberId),

        // PT tabs
        PT_SESSION_QUERY_KEYS.BY_MEMBER(memberId),
        PT_SESSION_PACK_QUERY_KEYS.BY_MEMBER(memberId),
      ];

      keys.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    },
    [queryClient],
  );
}
